import type { PromotionCountFilters } from '../hooks/queries/usePromotionCount';
import type { ConstraintGroup, FormData } from '../components/OptimizePlan/types';
import type { FormFieldConfig } from '../../../config/eventsConfig';
import type { LimitConstraintFieldConfig } from '../../../config/optimizePlanConfig';

export interface ScopeSelection {
    attributeType: string;
    values: string[];
}

export interface ScopeAttributeSelection {
    attributeType: string;
    attributeId: string;
}

export function buildScopeFilters(scopeSelections: ScopeSelection[]): PromotionCountFilters {
    const inclusion: PromotionCountFilters['inclusion'] = [];
    for (const { attributeType, values } of scopeSelections) {
        for (const id of values) {
            inclusion.push({ attributeType, attributeId: id });
        }
    }
    return { inclusion, exclusion: [] };
}

/**
 * Flattens the Scope tab into the canonical `attributes_selections` array.
 *
 * The same array goes to `get_optimization_original_vals` and to
 * `run-optimisation`, and it is never narrowed to the level being requested: it
 * describes what the run is scoped to, while `attribute_level` separately says
 * which level the caller wants rows for.
 */
export function buildAttributesSelections(scopeSelections: ScopeSelection[]): ScopeAttributeSelection[] {
    return scopeSelections.flatMap(({ attributeType, values }) =>
        values.map((id) => ({ attributeType, attributeId: id })),
    );
}

const ORIGINAL_VALUE_KEY = 'original_value';
const FULL_YEAR_ORIGINAL_VALUE_KEY = 'full_year_original_value';

interface BuildOptimizationPayloadParams {
    planId: string;
    formData: FormData;
    constraintGroups: ConstraintGroup[];
    scopeSelections: ScopeSelection[];
    baseInputFields: FormFieldConfig[];
    limitConstraintFields: LimitConstraintFieldConfig[];
}

// Returns top-level numeric field values (e.g. keep_top), excluding those mapped to limit_constraints.
function buildTopLevelNumericFields(
    formData: FormData,
    baseInputFields: FormFieldConfig[],
    limitConstraintFieldNames: Set<string>,
): Record<string, number> {
    const result: Record<string, number> = {};
    for (const field of baseInputFields) {
        if (
            (field.type === 'numberInput' || field.type === 'numberWithContext') &&
            field.name &&
            !limitConstraintFieldNames.has(field.name)
        ) {
            const raw = formData[field.name];
            result[field.name] = (raw != null ? Number(raw) : field.defaultValue) ?? 0;
        }
    }
    return result;
}

/**
 * One `range_constraints` entry per KPI, its `level_details` grouped by level so
 * the payload mirrors the two-level accordion the user filled in.
 *
 * `default_value` carries the full-year original value, which the optimiser uses
 * as the untouched baseline — not the API's own `default_value` field.
 */
function buildRangeConstraintEntry(group: ConstraintGroup) {
    return {
        constraint_name: group.name,
        level_details: group.levels.map((block) => ({
            attribute_level: block.level,
            attribute_selections: block.rows.map((row) => ({
                attribute_name: row.attributeName,
                value:
                    group.inputType === 'number'
                        ? { min: row.input.single, max: row.input.single }
                        : { min: row.input.min, max: row.input.max },
                original_value: (row.fields[ORIGINAL_VALUE_KEY] as number | null) ?? null,
                default_value: (row.fields[FULL_YEAR_ORIGINAL_VALUE_KEY] as number | null) ?? null,
            })),
        })),
    };
}

export function buildOptimizationPayload({
    planId,
    formData,
    constraintGroups,
    scopeSelections,
    baseInputFields,
    limitConstraintFields,
}: BuildOptimizationPayloadParams) {
    const attributesSelections = buildAttributesSelections(scopeSelections);

    const limitConstraintFieldNames = new Set(limitConstraintFields.map((f) => f.formFieldName));

    const topLevelNumericFields = buildTopLevelNumericFields(formData, baseInputFields, limitConstraintFieldNames);

    const limit_constraints = limitConstraintFields.map((lcf) => ({
        constraint_name: lcf.constraintName,
        operator: lcf.operator,
        value: Number(formData[lcf.formFieldName] ?? 0),
    }));

    return {
        plan_id: planId,
        ...(attributesSelections.length > 0 && { attributes_selections: attributesSelections }),
        objective: formData.objective,
        optimization_start: formData.optimization_start_date,
        ...topLevelNumericFields,
        range_constraints: constraintGroups.map(buildRangeConstraintEntry),
        limit_constraints,
    };
}
